import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { HintTooltip } from "@/components/Interface/HintTooltip/HintTooltip";
import { SubscriptionCard } from "@/components/Layout/BaseLayouts/SubscriptionCard/SubscriptionCard";
import { BrandLoadingScreen } from "@/components/Layout/BrandLoadingScreen/BrandLoadingScreen";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import { useAlert } from "@/hooks/useAlert";
import { useLocalStorageFlag } from "@/hooks/useLocalStorageFlag";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import {
  transferTypes,
  useSimTransferStateInfo,
} from "@/hooks/useSimTransferStateInfo";
import { useStartAndFetchEsimChange } from "@/hooks/useStartAndFetchEsimChange";
import { useGetESimInfo, useStartESimChange } from "@/services/api";
import { AlertTypes } from "@/utils/atoms";
import {
  isEsim,
  isEsimchangeInProgress,
  isPhysicalSIM,
  isUnscannedEsimInfo,
} from "@/utils/deviceUtils";
import { capitalizeFirstLetter } from "@/utils/translationHelpers";

import { EsimTransferFlow } from "./EsimTransferFlow";
import { SimDetailActions } from "./SimDetailActions";
import { UnscannedQRCodeDetails } from "./UnscannedQRCodeDetails";

export type DetailsSection =
  | "reloadEsim"
  | "transferEsim"
  | "physicalToEsim"
  | "networkReset";

export const DetailsEsimDisplay = ({
  activeSection,
  setActiveSection,
  isBlocked,
}: {
  activeSection: DetailsSection | null;
  setActiveSection: (section: DetailsSection | null) => void;
  isBlocked: boolean;
}) => {
  const [simId] = useSimIdFromQueryParam();
  const showAlert = useAlert();
  const { t: t2 } = useTranslation();
  const { data: simDetail, refetch: refetchSimDetail } = useDisplayedSim();
  const { shouldShowError, setErrorFired } = useLocalStorageFlag(
    "detailsEsimDisplayErrorFiredAt",
  );
  const { data: esimInfo } = useGetESimInfo(
    { simIds: [Number(simId)] },
    {
      query: {
        enabled: !isNaN(parseInt(simId)),
        onError: () => {
          if (shouldShowError()) {
            showAlert({
              type: AlertTypes.info,
              text: t2("Error.eSimError"),
            });
            setErrorFired();
          }
        },
      },
    },
  );
  const { t } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.details.sections.details",
  });

  const {
    startChange: startPhysicalToEsim,
    changeInProgress: physicalToEsimInProgress,
  } = useStartAndFetchEsimChange(
    refetchSimDetail,
    isEsimchangeInProgress,
    simId ? Number(simId) : undefined,
    useStartESimChange,
    transferTypes.PHYSICALTOESIM,
  );
  const startedPhysicalToEsim = useSimTransferStateInfo(
    simId,
    transferTypes.PHYSICALTOESIM,
  );

  // A switch started elsewhere (or before a page reload) still shows up as
  // in-progress from the API - reopen the inline flow so the user lands on
  // its waiting/ready state instead of the closed card. `esimchangeInProgress`
  // is shared with the eSIM-side TRANSFER flow (SimDetailActions has its own
  // copy of this effect for that case), so this must stay scoped to physical
  // SIMs - otherwise a sim that already converted to eSIM could still latch
  // onto "physicalToEsim" from a stale/overlapping transfer flag.
  useEffect(() => {
    if (
      simDetail &&
      isPhysicalSIM(simDetail) &&
      activeSection === null &&
      physicalToEsimInProgress
    ) {
      setActiveSection("physicalToEsim");
    }
  }, [activeSection, physicalToEsimInProgress, setActiveSection, simDetail]);

  // Once the conversion completes, `simDetail` is no longer a physical SIM,
  // so the `isPhysicalSIM` branch below (which alone knows how to render
  // "physicalToEsim") stops being reached at all - left uncleared, this
  // section would stay stuck non-null forever, permanently blocking every
  // other card on the page via `isAnySectionActive` in Details.tsx.
  useEffect(() => {
    if (activeSection === "physicalToEsim" && simDetail && isEsim(simDetail)) {
      setActiveSection(null);
    }
  }, [activeSection, setActiveSection, simDetail]);

  const hasEsimInfo = !!esimInfo && esimInfo.length > 0;

  // This might be disabled if there is a change to the sim in progress
  const simCanSwitchToEsim = simDetail?.simActions?.simActions?.includes(
    "canChangeTripleToESim",
  );

  if (!simDetail) {
    return <BrandLoadingScreen />;
  }

  if (isEsim(simDetail)) {
    // Reload/transfer take over this card's spot entirely while open - see
    // SimDetailActions, which renders its own SubscriptionCard for that.
    if (
      hasEsimInfo &&
      !isUnscannedEsimInfo(esimInfo[0]) &&
      (activeSection === "reloadEsim" || activeSection === "transferEsim")
    ) {
      return (
        <SimDetailActions
          esimInfo={esimInfo}
          simId={simId}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          isBlocked={isBlocked}
        />
      );
    }

    return (
      <SubscriptionCard dataTestid="esim-profile" title={t("esimProfile")}>
        {hasEsimInfo ? (
          isUnscannedEsimInfo(esimInfo[0]) ? (
            <UnscannedQRCodeDetails simDetail={simDetail} />
          ) : (
            <SimDetailActions
              esimInfo={esimInfo}
              simId={simId}
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              isBlocked={isBlocked}
            />
          )
        ) : (
          <LoadingSpinner />
        )}
      </SubscriptionCard>
    );
  }

  if (isPhysicalSIM(simDetail)) {
    if (activeSection === "physicalToEsim") {
      return (
        <EsimTransferFlow
          dataTestid="physical-to-esim"
          cardTitle={t("switchFromPhysicalToEsim.screens.confirm.title")}
          confirmDescription={
            <Trans
              t={t}
              i18nKey="switchFromPhysicalToEsim.screens.confirm.text"
              components={[
                <a
                  key="0"
                  className="underline"
                  href={t2("links.isMyDeviceESIMCompatible")}
                  target="_blank"
                  rel="noreferrer"
                />,
              ]}
            />
          }
          onConfirm={() => startPhysicalToEsim?.({ simId: parseInt(simId) })}
          confirmDisabled={!startPhysicalToEsim}
          onClose={() => setActiveSection(null)}
          startedChange={startedPhysicalToEsim}
          changeInProgress={physicalToEsimInProgress}
          matchingId={simDetail.matchingId}
          waitingText={t("changeEsim.waitingText")}
          readyTitle={t("changeEsim.qrCodeTitle")}
          readyDescription={t("changeEsim.qrCodeDone")}
          readyLink={
            <a
              className="text-body-xs text-foreground-primary underline"
              href={t2("layout.tiles.qrCodeTile.instructionsLink")}
              target="_blank"
              rel="noreferrer"
            >
              {t("instructionsLinkText")}
            </a>
          }
        />
      );
    }

    return (
      <SubscriptionCard
        dataTestid="change-to-esim"
        className="!gap-1"
        title={t("changeToEsim")}
        action={
          simCanSwitchToEsim ? (
            <Button
              dataTestid="change-to-esim-button"
              size="sm"
              variant="primary"
              disabled={isBlocked}
              onClick={() => setActiveSection("physicalToEsim")}
            >
              {capitalizeFirstLetter(t("portal:common.buttons.switch"))}
            </Button>
          ) : (
            <HintTooltip
              dataTestid="change-to-esim-button-hint"
              content={t("disabledTitle")}
              align="right"
            >
              <Button
                dataTestid="change-to-esim-button"
                size="sm"
                variant="primary"
                disabled
              >
                {capitalizeFirstLetter(t("portal:common.buttons.switch"))}
              </Button>
            </HintTooltip>
          )
        }
      >
        <p className="text-body-xs leading-none text-foreground-secondary">
          {t("esimHint")}
        </p>
      </SubscriptionCard>
    );
  }

  return null;
};
